import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import Button from '../../../components/general/Button';
import PageError from '../../../components/general/PageError';
import { color_shades_dark, color_shades_darker, color_shades_darkest, color_colors_ocean } from '../../../constants/colors';
import MedicalIntegrationConfigModel from '../../../models/organizations/MedicalIntegrationConfigModel';
import MedicalIntegrationConfigContainer from '../../../hooks/MedicalIntegrationConfigContainer';
import MedicalIntegrationConfigModal, { PRACTICE_MANAGEMENT_SYSTEMS } from './MedicalIntegrationConfigModal';

const addIcon = require('../../../content/images/add-circle-dark.svg');

interface MedicalIntegrationConfigSettingsProps {
    organizationId: string;
    title?: string;
    // Extra actions shown beside each configuration's Edit link
    renderConfigActions?: (config: MedicalIntegrationConfigModel) => React.ReactNode;
}

export const getPmsDisplayName = (integrationType: string): string => {
    return PRACTICE_MANAGEMENT_SYSTEMS.find(pms => pms.value === integrationType)?.label ?? integrationType;
};

// Requires a MedicalIntegrationConfigContainer.Provider above it
const MedicalIntegrationConfigSettings: React.FC<MedicalIntegrationConfigSettingsProps> = ({
    organizationId,
    title = 'PMS Configurations',
    renderConfigActions
}) => {
    const medicalConfigContainer = MedicalIntegrationConfigContainer.useContainer();
    const [editingConfig, setEditingConfig] = useState<MedicalIntegrationConfigModel | null>(null);
    const [saveErrors, setSaveErrors] = useState<string[]>([]);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [hasLoaded, setHasLoaded] = useState(false);

    useEffect(() => {
        setHasLoaded(false);
        medicalConfigContainer.loadConfigsByOrganizationId(organizationId).then(() => setHasLoaded(true));
    }, [organizationId]);

    const configs = medicalConfigContainer.medicalConfigs.filter(config => config && config.id);

    const startNewConfig = () => {
        setSuccessMessage(null);
        setSaveErrors([]);
        setEditingConfig({ organizationId, clientName: '', configuration: '{}', integrationType: '' });
    };

    const startEditConfig = (config: MedicalIntegrationConfigModel) => {
        setSuccessMessage(null);
        setSaveErrors([]);
        setEditingConfig(config);
    };

    const saveConfig = async (model: MedicalIntegrationConfigModel) => {
        const isNew = !model.id;
        setSaveErrors([]);
        const result = isNew
            ? await medicalConfigContainer.createConfig(model)
            : await medicalConfigContainer.updateConfig(model.id, model);

        if (result.resultType === "Ok") {
            // Reload after create so the new config comes back with its ID
            if (isNew) {
                await medicalConfigContainer.loadConfigsByOrganizationId(organizationId);
            }
            setEditingConfig(null);
            setSuccessMessage(`${model.clientName} ${isNew ? 'created' : 'updated'}`);
        } else {
            setSaveErrors([`Failed to ${isNew ? 'create' : 'update'} configuration: ` + (result.errors?.join(', ') || 'Unknown error')]);
        }
    };

    return (
        <div>
            <Header>
                <Title>{title}</Title>
                <AddButton
                    themes={['icon']}
                    leftComponent={() => <><img className="left-img" src={addIcon} alt="" /><ScreenReaderText>Add configuration</ScreenReaderText></>}
                    onClick={startNewConfig}
                />
            </Header>

            {!hasLoaded ? (
                <EmptyState>Loading configurations...</EmptyState>
            ) : medicalConfigContainer.errors.length > 0 && configs.length === 0 ? (
                <PageError errors={['Failed to load configurations: ' + medicalConfigContainer.errors.join(', ')]} />
            ) : configs.length === 0 ? (
                <EmptyState>No PMS configurations yet. Click the + button to add one.</EmptyState>
            ) : (
                <ConfigList>
                    {configs.map(config => (
                        <ConfigRow key={config.id} onClick={() => startEditConfig(config)}>
                            <ConfigInfo>
                                <ConfigNameRow>
                                    <ConfigName>{config.clientName}</ConfigName>
                                    <PmsBadge>{getPmsDisplayName(config.integrationType)}</PmsBadge>
                                </ConfigNameRow>
                                <ConfigIdRow>
                                    <ConfigId>{config.id}</ConfigId>
                                    <CopyIdButton value={config.id} label="Copy ID" />
                                </ConfigIdRow>
                            </ConfigInfo>
                            <ConfigActions>
                                {renderConfigActions?.(config)}
                                <TextLinkButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        startEditConfig(config);
                                    }}
                                >
                                    Edit
                                </TextLinkButton>
                            </ConfigActions>
                        </ConfigRow>
                    ))}
                </ConfigList>
            )}

            {successMessage && <PageError errors={[successMessage]} type="success" />}

            {editingConfig && (
                <MedicalIntegrationConfigModal
                    config={editingConfig}
                    isSaving={medicalConfigContainer.isSaving}
                    errors={saveErrors}
                    onClose={() => setEditingConfig(null)}
                    onSave={saveConfig}
                />
            )}
        </div>
    );
};

interface CopyIdButtonProps {
    value: string;
    label?: string;
}

// Small inline "Copy" link that briefly confirms the copy
export const CopyIdButton: React.FC<CopyIdButtonProps> = ({ value, label = 'Copy' }) => {
    const [copied, setCopied] = useState(false);
    return (
        <TextLinkButton
            title={`Copy ${value}`}
            onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(value);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            }}
        >
            {copied ? 'Copied!' : label}
        </TextLinkButton>
    );
};

export const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    padding-bottom: 4px;
    gap: 8px;
`;

export const Title = styled.span`
    margin: 0;
    font-family: Muli;
    font-size: 14px;
    font-weight: 600;
    color: ${color_shades_darkest};
    line-height: 1;
`;

const AddButton = styled(Button)`
    height: 24px;
    width: 24px;
    min-height: 24px;
    min-width: 24px;
    margin-top: 4px;
    margin-bottom: 4px;
`;

export const EmptyState = styled.div`
    font-family: Muli;
    font-size: 14px;
    font-style: italic;
    color: ${color_shades_darker};
    text-align: center;
    padding: 32px;
    border: dashed 1px ${color_shades_dark};
    border-radius: 8px;
`;

const ConfigList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ConfigRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding: 12px 16px;
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
    background-color: #ffffff;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: ${color_colors_ocean};
        background-color: rgba(46, 125, 206, 0.05);
    }
`;

const ConfigActions = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    flex-shrink: 0;
`;

const ConfigInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
`;

const ConfigNameRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const ConfigName = styled.span`
    font-family: Muli;
    font-weight: 600;
    font-size: 14px;
    color: ${color_shades_darkest};
`;

export const PmsBadge = styled.span`
    font-family: Muli;
    font-size: 11px;
    font-weight: 700;
    color: ${color_colors_ocean};
    background-color: rgba(46, 125, 206, 0.1);
    border-radius: 10px;
    padding: 2px 8px;
`;

const ConfigIdRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
`;

const ConfigId = styled.span`
    font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
    font-size: 12px;
    color: ${color_shades_darker};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const TextLinkButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    color: ${color_colors_ocean};
    font-family: Muli;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
    border-bottom: 1px dashed ${color_colors_ocean};

    &:hover {
        color: #0056b3;
        border-bottom-color: #0056b3;
    }
`;

export default MedicalIntegrationConfigSettings;

// Read by screen readers but not shown
const ScreenReaderText = styled.span`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
`;
